export type ApiKey = {
  id: number;
  name: string;
  /** Null on keys created before the cabinet started asking for a product. */
  service_tool: string | null;
  service_tool_label: string | null;
  /** Catalogue slug of the tool's own product; null with `service_tool`. */
  product: string | null;
  /**
   * The tool's calls this month for the whole company — the gateway counts per
   * tool, not per key, so every key on the tool shows the same figure. Null
   * with `service_tool`.
   */
  calls_this_month: number | null;
  usage_scope: "product";
  created_at: string;
  /** Always null: the gateway checks keys in Redis and Sanctum never sees them used. */
  last_used_at: string | null;
};

export type ApiKeyListResponse = {
  data: ApiKey[];
};

/**
 * `token` is present only in the create response — the back office stores a hash,
 * so this is the single moment the key can be shown to anyone.
 */
export type ApiKeyCreatedResponse = {
  data: ApiKey;
  token: string;
};

export type CreateApiKeyPayload = {
  name: string;
  service_tool?: string | null;
};

/**
 * A subscription exists once the company has a limit on the tool or an API key
 * issued against it — either one is enough. From there: `active` while calls
 * remain, `limit_reached` at either cap, `expired` past the date.
 */
export type SubscriptionStatus = "active" | "limit_reached" | "expired";

export type UsagePeriod = {
  /** "YYYY-MM". */
  period: string;
  used: number;
  /** Null when the tool has no monthly cap. */
  remaining: number | null;
  percent: number | null;
  /**
   * Unique cargo numbers seen this month. Null on every tool without a unique
   * cap — the panel then draws one bar instead of two.
   */
  unique_used: number | null;
  unique_remaining: number | null;
  unique_percent: number | null;
};

export type UsageHistoryEntry = {
  period: string;
  used: number;
  percent: number | null;
  /** Null on tools without a unique cap, exactly as in `UsagePeriod`. */
  unique_used: number | null;
  unique_percent: number | null;
};

/** An axis the gateway blocks on once it is used up. */
export type LimitAxis = "calls" | "unique";

/**
 * What the limit-reached card needs. Present on every row; `reached_on` is
 * empty while there is room on both axes.
 */
export type SubscriptionLimit = {
  reached_on: LimitAxis[];
  /** "YYYY-MM-DD" — the first day of next month, when the counter starts over. */
  resets_on: string;
  /** Where "Upgrade limit" and "Buy More Calls" go. */
  upgrade_url: string;
};

export type Subscription = {
  service_tool: string;
  /**
   * Slug of the catalogue product this tool stands for on its own — never
   * `global-tracking`, which is two tools rather than one.
   */
  product: string;
  /** Back office label; the product catalogue name wins where the two differ. */
  label: string;
  monthly_limit: number | null;
  /**
   * Cap on distinct container/AWB numbers per month, on top of `monthly_limit`.
   * Only the tracking tools carry one; null everywhere else.
   */
  monthly_unique_limit: number | null;
  /** ISO date, or null for an open-ended subscription. */
  expires_at: string | null;
  /** The unique-shipments cap has an expiry of its own; null where there is no such cap. */
  unique_expires_at: string | null;
  status: SubscriptionStatus;
  limit: SubscriptionLimit;
  usage: UsagePeriod;
  /** Closed months, newest first, at most twelve — only months actually stored. */
  history: UsageHistoryEntry[];
  /**
   * "YYYY-MM" of the earliest stored month, or null when nothing is stored yet.
   * Months before it were never recorded, not quiet, so nothing is drawn for them.
   */
  history_since: string | null;
};

export type SubscriptionListResponse = {
  data: Subscription[];
};

export type QubicKind = "api" | "web";

/** The Store's category tabs; "All Products" is the absence of one. */
export type QubicCategory = "data" | "tracking" | "schedules" | "rates" | "intelligence" | "calculators";

export type QubicBadge = "popular" | "new";

/** What a company that does not own the product is offered. */
export type QubicAvailability = "available" | "coming_soon";

/**
 * One product in the Qubics Store catalogue, marked with what the company
 * owns. A product joins its subscription rows through `service_tools`: each
 * tool listed there has at most one row in `/api/subscriptions`.
 */
export type Qubic = {
  slug: string;
  name: string;
  /** A file name such as "container-tracking.png" — see `cubeUrl`. */
  cube: string;
  /** Public product page; null for a product without one. */
  url: string | null;
  /** Null for every product for now: hide the link rather than point it at "#". */
  docs_url: string | null;
  blurb: string;
  kinds: QubicKind[];
  category: QubicCategory;
  badge: QubicBadge | null;
  availability: QubicAvailability;
  /** Empty for products the gateway does not meter — those are never owned. */
  service_tools: string[];
  owned: boolean;
  owned_tools: string[];
};

export type QubicListResponse = {
  data: Qubic[];
};

export type Company = {
  id: number;
  name: string | null;
};

/** `provider` is null for accounts that sign in with a password. */
export type AuthProvider = "google" | "github";

export type Profile = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  country_code: string | null;
  dial_code: string | null;
  /** The profile card's three lines of copy. Each is null until it is written. */
  headline: string | null;
  position: string | null;
  location: string | null;
  /**
   * Absolute URLs on the back office, null while nothing is uploaded — the
   * card draws its own placeholder then. Read-only: they follow the stored
   * path and change only through the upload routes.
   */
  avatar_url: string | null;
  cover_url: string | null;
  provider: AuthProvider | null;
  /** Back-office admin flag; gates Logistics Data and similar panes. */
  is_admin: boolean;
  company: Company | null;
};

/** Which of the profile's two images a request is about. */
export type ProfileImageKind = "avatar" | "cover";

export type ProfileResponse = {
  data: Profile;
};

/**
 * `PATCH /api/user/me` is partial apart from `first_name`: a key that is not
 * sent is left alone. The settings form sends the contact block, the profile
 * card sends the three card fields, and neither disturbs the other.
 */
export type UpdateProfilePayload = {
  first_name: string;
  last_name: string;
  company_name?: string;
  country_code?: string;
  dial_code?: string;
  phone?: string;
  headline?: string | null;
  position?: string | null;
  location?: string | null;
};

export type UpdatePasswordPayload = {
  current_password: string;
  password: string;
  password_confirmation: string;
};

export type DeleteAccountPayload = {
  confirmation: string;
  password?: string;
};

export type ValidationErrorResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

/** Ocean or air — sea and air shipments share one saved list. */
export type ShipmentType = "SEA" | "AIR";

/**
 * A shipment saved from the tracking widget. Cards belong to the company, so
 * `created_by` says which colleague tracked it.
 *
 * The shape follows the widget's own DTO; the cabinet only reads part of it.
 */
export type Shipment = {
  id: number;
  reference: string;
  /**
   * Which number `reference` is. Null on cards saved before the widget sent
   * it, "PENDING" on a card added before its number existed — there the
   * reference is a placeholder the back office issued, not a carrier's number.
   */
  type: ReferenceType | PendingType | null;
  shipment_type: ShipmentType | null;
  carrier_scac: string | null;
  carrier_name: string | null;
  status: ShipmentStatus | null;
  equipments_count: number;
  /** Container size and type, e.g. "40HC". Null on air and on unmatched cards. */
  size_type: string | null;
  location: {
    from: string | null;
    to: string | null;
    /** Last known position. The map falls back to the route when it is null. */
    pin: Coordinate | null;
  };
  date: {
    /** Departure: actual when `from_actual`, otherwise estimated. */
    from: string | null;
    from_actual: boolean;
    /** Arrival: actual when `to_actual`, otherwise estimated. */
    to: string | null;
    to_actual: boolean;
    /** Qubictron's own arrival estimate. Later than `to` means the ship is late. */
    to_predictive: string | null;
  };
  tags: string[];
  created_at: string | null;
  updated_at: string | null;
  created_by: { id: number; name: string } | null;
  /**
   * How the card got here. "web" — somebody saved it in the app; "api" — the
   * company tracked the number with its own key, straight on the gateway, and
   * the back office picked it up from the call afterwards. An API card has no
   * `created_by`: the gateway does not say which colleague made the call.
   */
  source: ShipmentSource;
  /** When the number last appeared in the company's own API calls. */
  api_last_seen_at: string | null;
  /** Set on the Cargo tab; null until somebody picks one there. */
  load_type: LoadType | null;
};

export type ShipmentSource = "web" | "api";

/** FCL fills a container; LCL shares one, so its cargo is listed line by line. */
export type LoadType = "FCL" | "LCL";

export type WeightUnit = "mt" | "kg" | "lb";

export type VolumeUnit = "CBM" | "CFT";

/**
 * One line of cargo inside a container. Decimals come back as fixed-scale
 * strings ("2400.000") so no float noise reaches the form.
 */
export type ShipmentCargoLine = {
  id: number;
  position: number;
  name: string | null;
  package_type: string | null;
  package_quantity: number | null;
  gross_weight: string | null;
  weight_unit: WeightUnit;
  volume: string | null;
  volume_unit: VolumeUnit;
};

export type ShipmentContainer = {
  id: number;
  position: number;
  number: string | null;
  container_type: string | null;
  /** Null means "Not dangerous". */
  dg_class: string | null;
  cargo_group: string | null;
  gross_weight_mt: string | null;
  /** The container's own note, saved with the grid — not the notes feed. */
  note: string | null;
  /** Empty for FCL. */
  cargo_lines: ShipmentCargoLine[];
};

/**
 * `GET /api/shipments/{id}`: the saved card plus everything the detail page
 * draws — the identity fields, the container grid and the tab badges. The
 * contract is `docs/shipment-detail-api.md` in the back office.
 */
export type ShipmentDetail = Shipment & {
  booking_number: string | null;
  master_bl: string | null;
  house_bl: string | null;
  item_name: string | null;
  item_description: string | null;
  containers: ShipmentContainer[];
  counts: { documents: number; charges: number; notes: number };
};

/**
 * `PUT /api/shipments/{id}` with the fields the detail page edits. The widget
 * calls the same route with its own fields; the response is the card alone.
 */
export type IdentityPayload = {
  load_type: LoadType;
  booking_number: string | null;
  master_bl: string | null;
  house_bl: string | null;
  item_name: string | null;
  item_description: string | null;
};

export type CargoLinePayload = {
  name: string | null;
  package_type: string | null;
  package_quantity: number | null;
  gross_weight: number | null;
  weight_unit: WeightUnit;
  volume: number | null;
  volume_unit: VolumeUnit;
};

export type ContainerPayload = {
  number: string | null;
  container_type: string | null;
  dg_class: string | null;
  cargo_group: string | null;
  gross_weight_mt: number | null;
  note: string | null;
  cargo_lines: CargoLinePayload[];
};

/**
 * `PUT /api/shipments/{id}/cargo`: the whole grid, in display order. A saved
 * container whose number is still in the list keeps its id and documents.
 */
export type CargoPayload = { containers: ContainerPayload[] };

/**
 * A file on a shipment or on one of its containers. `size_label`, `extension`
 * and `container_number` are what the drawer shows, so they are read as sent.
 */
export type ShipmentDocument = {
  id: number;
  name: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  size_label: string;
  extension: string;
  /** Null for a shipment-level document. */
  container_id: number | null;
  container_number: string | null;
  uploaded_by: { id: number; name: string } | null;
  created_at: string;
  /** Relative to the back office origin — see `documentFileUrl`. */
  url: string;
};

export type ShipmentDocumentListResponse = {
  documents: ShipmentDocument[];
  total_count: number;
};

export type UploadDocumentOptions = {
  name?: string;
  /** Omit for a shipment-level document. */
  container_id?: number;
};

export type Coordinate = { lat: number; lng: number };

/** Which kind of number the card was tracked by. "AWB" is the air one. */
export type ReferenceType = "CONTAINER" | "BL" | "BOOKING" | "AWB";

/** Stands in for a reference type on a card saved with no number yet. */
export type PendingType = "PENDING";

export const PENDING_TYPE: PendingType = "PENDING";

export type ShipmentStatus = "UNKNOWN" | "PLANNED" | "IN_TRANSIT" | "DELIVERED" | "CANCELLED";

/**
 * Body of a follow. Only `reference` and `shipment_type` are required — the
 * rest is whatever the search turned up, and the back office stores it as is
 * so the tracking widget reads back the card it expects.
 */
export type FollowShipmentPayload = {
  reference: string;
  shipment_type: ShipmentType;
  type: ReferenceType | null;
  carrier_scac: string | null;
  carrier_name: string | null;
  status: ShipmentStatus | null;
  equipments_count: number;
  /** JSON array of container types, matching what the widget writes. */
  size_type: string | null;
  location: {
    from: string | null;
    to: string | null;
    transport: "SEA" | "AIR";
    pin: Coordinate | null;
  };
  date: {
    from: string | null;
    from_actual: boolean;
    to: string | null;
    to_actual: boolean;
    to_predictive: string | null;
  };
};

/**
 * Body of a numberless card. The reference is the back office's to invent, so
 * only the mode and the route it is meant to run travel from here.
 */
export type PendingShipmentPayload = {
  shipment_type: ShipmentType;
  origin: string | null;
  destination: string | null;
};

export type ShipmentListResponse = {
  total_count: number;
  cards: Shipment[];
  count_by_status: { status: string; count: number }[];
};
